import { Control, useForm } from "react-hook-form";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import {
  IEntityMinimalModel,
  IGenericRequestParameters,
  InferSortByOptions,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import {
  GenericEntityVirtualizedEntitySelectFormFieldProps,
  GenericVirtualizedEntitySelectFormFieldControllerWrapper,
} from "../../../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedEntitySelectFormFieldControllerWrapper";
import { useEffect, useMemo } from "react";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { NotAllowed } from "../../../common/misc/UIconstants";
import { getNotebookEntryStringLabel } from "../../../ELN/LabNotebookEntry/LabNotebookEntry";
import { getAuditLogStringLabel } from "../../../AuditLog/AuditLog";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { auditLogConstants } from "../../../api/AuditLog";
import { labNotebookEntriesConstants } from "../../../ELN/types/LabNotebookEntry";

export interface GenericEntitySelectWidgetProps<
  Entity extends GenericEntity,
  SuggestionModel extends IEntityMinimalModel,
  Filters extends StringIndexedDict & IGenericRequestParameters<SuggestionModel, InferSortByOptions<Filters>>
> extends Omit<GenericEntityVirtualizedEntitySelectFormFieldProps<Entity, SuggestionModel, Filters>, "control"> {
  control?: Control<Entity, any, Entity>;
}

export const GenericEntitySelectWidget = <
  Entity extends GenericEntity,
  SuggestionModel extends IEntityMinimalModel & Partial<Entity>,
  Filters extends StringIndexedDict & IGenericRequestParameters<SuggestionModel, InferSortByOptions<Filters>>
>({
  entityTypeId,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntitySelectWidgetProps<Entity, SuggestionModel, Filters>>) => {
  const { control, resetField } = useForm<Entity>({ defaultValues: componentProps?.defaultValue as any });

  const activeControl = useMemo(() => {
    if (!!componentProps?.control) return componentProps.control;
    return control;
  }, [control, componentProps?.control]);
  useEffect(() => {
    // Only reset if no control is passed
    if (!componentProps?.control) resetField((componentProps?.id as any) ?? `${entityTypeId}-Select`);
  }, [componentProps?.control, componentProps?.id, entityTypeId, resetField]);

  const suggestedEntityProps = useDefaultGenericEntitySelectWidget({ entityTypeId });
  const defaultComponentProps = useMemo(() => {
    const { entityConstants, sortingOptions } = genericEntityConstants[entityTypeId];
    return {
      // Generic values
      id: `${entityTypeId}-Select`,
      control: activeControl,
      // Default overrides
      ...suggestedEntityProps,
      // Local overrides
      ...componentProps,
      // Forced props
      entityConstants: entityConstants,
      orderByOptions: sortingOptions,
    };
  }, [activeControl, componentProps, entityTypeId, suggestedEntityProps]);

  const { canReadIndex } = useEntityPermissions({ entityTypeId });
  if (!canReadIndex) return NotAllowed;
  return (
    <GenericVirtualizedEntitySelectFormFieldControllerWrapper<Entity, SuggestionModel, Filters>
      {...(defaultComponentProps as any)}
    />
  );
};

const useDefaultGenericEntitySelectWidget: <
  Entity extends GenericEntity,
  SuggestionModel extends IEntityMinimalModel & Partial<Entity>,
  Filters extends StringIndexedDict & IGenericRequestParameters<SuggestionModel, InferSortByOptions<Filters>>
>({
  entityTypeId,
}: {
  entityTypeId: GenericEntityConstantsEntities;
}) => Partial<GenericEntitySelectWidgetProps<Entity, SuggestionModel, Filters>> = ({ entityTypeId }) => {
  switch (entityTypeId) {
    case "auditLogs":
      return {
        overrideItemRenderer: (auditLog) => (
          <div className="flex align-center gap-5">
            <LucideIcon name={auditLogConstants.icon} color="var(--primary)" />
            {getAuditLogStringLabel({ auditLog: auditLog as any })}
          </div>
        ),
        overrideSelectedRenderer: (auditLog) => getAuditLogStringLabel({ auditLog: auditLog as any }),
      };
    case "notebookEntries":
      return {
        overrideItemRenderer: (entry) => (
          <div className="flex align-center gap-5">
            <LucideIcon name={labNotebookEntriesConstants.icon} color="var(--primary)" />
            {getNotebookEntryStringLabel({ entry: entry as any })}
          </div>
        ),
        overrideSelectedRenderer: (entry) => getNotebookEntryStringLabel({ entry: entry as any }),
      };
    default:
      return {};
  }
};
